import React, { FC, useCallback, useContext, useMemo } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { ICronJob } from '../../models/CronJob';
import { Link } from 'react-router-dom';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { PendingTasksFetcher } from './PendingTasks';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useTranslation } from 'react-i18next';
import { LazyUser } from '../LazyData/LazyUser';
import { apiScheduled } from '../../resources/api/apiScheduled';
import { useDialog } from '../../hooks/useDialog';
import TableBody from '@mui/material/TableBody';

interface IProps {
  pendingTask?: ICronJob;
  closeDialog: () => void;
}

const { useCancelJob } = apiScheduled;

export const PendingTaskDetailsDialog: FC<IProps> = ({ pendingTask, closeDialog }) => {
  const { onRefresh } = useContext(PendingTasksFetcher.Context);
  const { execute: deleteScheduledJob, Guard: GuardDelete } = useCancelJob(pendingTask?.id);

  const handleError = useApiErrorHandler();
  const { t } = useTranslation();

  const requestFields = useMemo(() => {
    if (!pendingTask) return [];
    return Object.entries(pendingTask.request).map(([field, value]) => ({
      field,
      value,
    }));
  }, [pendingTask?.request]);

  const { isOpen: isDeleteOpen, openUrl: deleteUrl, close: abortDeletion } = useDialog('delete');

  const onDelete = useCallback(async () => {
    try {
      await deleteScheduledJob();
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [deleteScheduledJob]);

  return (
    <Dialog open={!!pendingTask} onClose={closeDialog}>
      <DialogTitle>{t('pendingTasks.detailsDialog.title')}</DialogTitle>
      <DialogContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('common.tableHeader.id')}:</TableCell>
              <TableCell>{pendingTask?.id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('pendingTasks.detailsDialog.operationLabel')}:</TableCell>
              <TableCell>{pendingTask?.operation}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('pendingTasks.detailsDialog.ownerLabel')}:</TableCell>
              <TableCell>{pendingTask?.ownerUuid ? <LazyUser id={pendingTask.ownerUuid} /> : null}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('common.tableHeader.status')}:</TableCell>
              <TableCell>{pendingTask?.status}</TableCell>
            </TableRow>

            {requestFields.map(({ field, value }) => (
              <TableRow key={field}>
                <TableCell>{field}</TableCell>
                <TableCell>{JSON.stringify(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Close</Button>
        <GuardDelete>
          <DangerousButton component={Link} to={deleteUrl} replace={true}>
            {t('common.button.delete')}
          </DangerousButton>
        </GuardDelete>
      </DialogActions>

      <ConfirmationDialog
        open={isDeleteOpen}
        title={t('pendingTasks.deleteDialog.title')}
        message={t('pendingTasks.deleteDialog.message')}
        confirm={onDelete}
        abort={abortDeletion}
        dangerous
      />
    </Dialog>
  );
};
